import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  ConfirmDelete,
  ContentNugget,
  PopoverFilterMenu,
  addHttpIfMissing,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useGetLinkPreviewQuery } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  PRIORITY_TYPES,
  PriorityTask,
  PriorityTaskPost,
  PriorityTaskStatusType,
  PriorityTaskType,
  TaskStatusCode,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Theme, SxProps, Typography, ClickAwayListener } from '@mui/material';

import { TaskCardNuggetContent } from '../atoms/TaskCardNuggetContent';
import { TaskStatusChip } from '../atoms/TaskStatusChip';
import { TASK_CARD_MIN_WIDTH } from '../constants/priorities.constants';
import { transformTaskContentsToNuggets } from '../utils/tasks.utils';

const TASK_CARD_STRINGS = appStrings.modules.priorities.taskCard;

export type TaskCardProps = Readonly<{
  task: PriorityTask;

  disabled?: {
    disableNuggetClick: boolean;
    disableStatus?: boolean;
  };

  expanded?: boolean;
  isInSidebar?: boolean;
  isHighlighted?: boolean;
  isDraggable?: boolean;

  topRightElement?:
    | {
        type: 'ELLIPSIS_MENU';
      }
    | { type: 'ELEMENT'; element: ReactNode };

  restrictWidth?: boolean;
  opacity?: number;
  taskTypes: PriorityTaskType[];
  taskStatuses: PriorityTaskStatusType[];
  deleteTask?: (taskId: number) => void;
  openEditTaskDialog?: (task: PriorityTask) => void;
  /** disables any other click handlers */
  onCardClick?: () => void;
  updateTask?: (task: Partial<PriorityTaskPost>) => Promise<void>;
  handleDuplicateTask?: (task: PriorityTask) => void;
}>;

export const TaskCard = ({
  task,
  disabled,
  expanded = false,
  isInSidebar = false,
  isHighlighted = false,
  isDraggable = false,
  topRightElement = { type: 'ELLIPSIS_MENU' },
  restrictWidth = true,
  opacity = 1,
  taskTypes,
  taskStatuses,
  deleteTask,
  openEditTaskDialog,
  onCardClick,
  updateTask,
  handleDuplicateTask,
}: TaskCardProps) => {
  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data: linkPreview } = useGetLinkPreviewQuery(task.content_url || '', {
    skip: !task.content_url,
  });

  const taskType = taskTypes?.find((type) => type.code === task.type_code);

  const contentTransformedToNuggets = transformTaskContentsToNuggets(task);

  const isThereInfoSection: boolean =
    !!task.due_date || !!task.priority || !!taskType?.display_name;

  const handleUpdateTaskStatus = async (statusCode: TaskStatusCode) => {
    const updatedTask: Partial<PriorityTaskPost> = {
      status_code: statusCode,
    };

    await updateTask?.(updatedTask);
  };

  const handleNuggetClick = (nugget: ContentNugget) => {
    // Handle tools specially using sticker card mapping
    if (nugget.variant === 'tool' && nugget.tool_info) {
      const stickerCard = getStickerCard(nugget.tool_info.tool_id as StickerCardId);

      if (stickerCard && stickerCard.onClick) {
        stickerCard.onClick();

        return;
      }
    }

    // Open external links in a new tab
    if (task.content_url) {
      window.open(addHttpIfMissing(task.content_url), '_blank');

      return;
    }

    // Use default handling for other nuggets
    handlePanelNuggetClick(nugget, navigate);
  };

  const externalLinkAsNugget: ContentNugget = {
    id: 0,
    slug: '',
    title: linkPreview?.title || '',
    description: linkPreview?.description || '',
    external_url: task.content_url || '',
    variant: 'resource',
    is_browsable: true,
    type: 'content',
    star_rating: 0,
    value_rating: 0,
    preview_media: {
      id: 0,
      media_url: linkPreview?.images?.[0] || '',
    },
  };

  return (
    <Box
      sx={containerSx(
        restrictWidth,
        !!onCardClick,
        !!disabled,
        expanded,
        isHighlighted,
        isInSidebar,
        isDraggable,
      )}
      onClick={onCardClick}
    >
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={TASK_CARD_STRINGS.deleteTask}
        bodyText={TASK_CARD_STRINGS.deleteTaskConfirmBody}
        handleDelete={() => deleteTask?.(task.id)}
      />
      {!disabled && (
        <PopoverFilterMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          categories={[
            {
              label: 'Actions',
              items: [
                {
                  label: TASK_CARD_STRINGS.editTask,
                  value: 'edit',
                  category: 'action',
                  icon: 'Edit05',
                  onClick: () => openEditTaskDialog?.(task),
                  closeOnClick: true,
                },
                {
                  label: TASK_CARD_STRINGS.duplicateTask,
                  value: 'duplicate',
                  category: 'action',
                  icon: 'Copy04',
                  onClick: () => handleDuplicateTask?.(task),
                  closeOnClick: true,
                },
                {
                  label: TASK_CARD_STRINGS.deleteTask,
                  value: 'delete',
                  category: 'action',
                  icon: 'Trash01',
                  onClick: () => setConfirmDeleteOpen(true),
                  closeOnClick: true,
                },
              ],
            },
          ]}
          singleLevel
        />
      )}

      {/* HEADER: TITLE/DESCRIPTION/ELLIPSIS MENU */}
      {/* only show bottom border if there is an info section */}
      <Box sx={taskHeaderContainerSx(isThereInfoSection)}>
        <Box sx={taskHeaderContentSx(opacity)}>
          <Box sx={taskTitleStatusContainerSx}>
            <Typography variant="text-lg-semibold" sx={taskTitleSx}>
              {task.title}
            </Typography>

            {/* STATUS */}
            {!disabled?.disableStatus && (
              <Box
                sx={statusChipContainerSx}
                className="status-chip"
                onClick={(event) => event.stopPropagation()}
              >
                <TaskStatusChip
                  statusCode={task.status_code}
                  allStatuses={taskStatuses}
                  updateTaskStatus={handleUpdateTaskStatus}
                  disabled={!!disabled}
                />
              </Box>
            )}
          </Box>
          <Typography variant="text-sm-regular" sx={taskDescriptionSx(expanded)}>
            {task.description}
          </Typography>
        </Box>

        {topRightElement.type === 'ELLIPSIS_MENU' && (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Box
              id="task-card-menu-button"
              sx={iconContainerSx(!disabled)}
              onClick={(event) => {
                event.stopPropagation();
                if (!disabled) {
                  setAnchorEl(event.currentTarget);
                }
              }}
            >
              <AppIcon
                iconName="DotsVertical"
                diameter="20px"
                color="Colors/Foreground/fg-quinary"
              />
            </Box>
          </ClickAwayListener>
        )}

        {topRightElement.type === 'ELEMENT' && topRightElement.element}
      </Box>

      {/* INFO SECTION */}
      <Box sx={infoSectionContainerSx(isThereInfoSection, opacity)}>
        {task.due_date && (
          <Box sx={infoSectionContentSx}>
            <AppIcon
              iconName="CalendarCheck01"
              diameter="20px"
              color="Colors/Foreground/fg-quinary"
            />

            <Typography
              variant="text-xs-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {new Date(task.due_date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          </Box>
        )}

        {task.priority && (
          <Box sx={infoSectionContentSx}>
            <AppIcon
              iconName="BellRinging01"
              diameter="20px"
              color="Colors/Foreground/fg-quinary"
            />

            <Typography
              variant="text-xs-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {PRIORITY_TYPES[task.priority]} Priority
            </Typography>
          </Box>
        )}

        {taskType?.display_name && (
          <Box sx={infoSectionContentSx}>
            <AppIcon iconName="Target04" diameter="20px" color="Colors/Foreground/fg-quinary" />

            <Typography
              variant="text-xs-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {taskType?.display_name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* CONTENT (NUGGET) SECTION */}
      {contentTransformedToNuggets.length > 0 &&
        contentTransformedToNuggets.map((nugget) => (
          <TaskCardNuggetContent
            key={nugget.id}
            nugget={nugget}
            onClick={() => !disabled?.disableNuggetClick && handleNuggetClick(nugget)}
          />
        ))}
      {/* EXTERNAL URL */}
      {task.content_url && (
        <TaskCardNuggetContent
          nugget={externalLinkAsNugget}
          onClick={() => handleNuggetClick(externalLinkAsNugget)}
        />
      )}
    </Box>
  );
};

const containerSx =
  (
    restrictWidth: boolean,
    isClickable?: boolean,
    isDisabled?: boolean,
    isClicked?: boolean,
    isHighlighted?: boolean,
    isInSidebar?: boolean,
    isDraggable?: boolean,
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',

    width: '100%',
    maxWidth: restrictWidth ? '100%' : `${TASK_CARD_MIN_WIDTH}px`,
    paddingY: backrs.spacing['spacing-2xl'],
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],

    cursor: isDraggable ? 'grab' : isClickable ? 'pointer' : 'default',

    '@media (hover: hover)': {
      '&:hover .status-chip': {
        opacity: 1,
      },
    },

    // selected state
    ...(isClicked && {
      border: `1px solid ${backrs.colors['Brand/300']}`,
      outline: `2px solid ${backrs.colors['Brand/500']}`,
      outlineOffset: '2px',
    }),

    // highlight state
    ...(isHighlighted && {
      border: `1px solid ${backrs.colors['Brand/300']}`,
    }),

    // basic hover state for the sidebar
    ...(isInSidebar && {
      '&:hover': {
        border: `1px solid ${backrs.colors['Brand/300']}`,
      },
    }),

    // outside of the sidebar, we use the standard hover state for cards
    ...(isClickable &&
      !isInSidebar &&
      !isDisabled && {
        transition: 'all 0.3s ease-in-out',

        cursor: 'pointer',
        '&:hover': {
          borderColor: isClicked
            ? backrs.colors['Brand/500']
            : backrs.colors['Colors/Foreground/fg-primary'],
          transform: 'translate(-2px, -2px)',
          boxShadow: backrs.shadows['shadow-lg'],
        },

        '&:active': {
          transform: 'none !important',
          boxShadow: 'none !important',
          transition: 'all 0s !important',
        },
      }),
  });

const taskHeaderContainerSx =
  (showBottomBorder: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-lg'],
    overflow: 'hidden',
    ...(showBottomBorder && {
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    }),
    paddingX: backrs.spacing['spacing-3xl'],
  });

const taskTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: `calc(${TASK_CARD_MIN_WIDTH}px - (${backrs.spacing['spacing-7xl']}))`,
});

const taskDescriptionSx =
  (expanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    ...(!expanded && {
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    maxWidth: `calc(${TASK_CARD_MIN_WIDTH}px - (${backrs.spacing['spacing-7xl']}))`,
  });

const iconContainerSx =
  (isClickable?: boolean): SxProps<Theme> =>
  () => ({
    cursor: isClickable ? 'pointer' : 'default',
  });

const taskHeaderContentSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    pb: backrs.spacing['spacing-2xl'],
    opacity,
  });

const infoSectionContainerSx =
  (shouldDisplay: boolean, opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: shouldDisplay ? 'flex' : 'none',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-md'],
    paddingX: backrs.spacing['spacing-3xl'],
    pt: backrs.spacing['spacing-2xl'],
    opacity,
  });

const infoSectionContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const taskTitleStatusContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

const statusChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-6xl'],
  opacity: 1,
  transition: 'opacity 0.2s ease-in-out',
  zIndex: 1,
  cursor: 'pointer',
  '@media (hover: hover)': {
    opacity: 0,
  },
});
